import { Alert, AlertTitle, Box, Paper, Typography } from "@mui/material";
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  getErrorMessage,
  getErrorSeverity,
  getErrorTitle,
} from "../api/errorMessages";
import type { ValidationResult } from "../api/types";
import { EmailInputForm, ValidationResultCard } from "../components";
import { useHealthStatus, useValidateEmail } from "../hooks";

export function ValidatePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email") || "";

  const {
    isHealthy,
    isLoading: isHealthLoading,
    isError: healthError,
  } = useHealthStatus();
  const validateMutation = useValidateEmail();
  const { mutate } = validateMutation;
  const hasAutoValidated = useRef(false);

  const handleValidate = (email: string) => {
    mutate(email);
    // Clear the URL param after validation to avoid confusion on refresh
    if (searchParams.has("email")) {
      setSearchParams({});
    }
  };

  // Auto-validate if email param is provided
  useEffect(() => {
    if (hasAutoValidated.current) {
      return;
    }

    if (!emailFromUrl || isHealthLoading) {
      return;
    }

    if (healthError || !isHealthy) {
      return;
    }

    hasAutoValidated.current = true;
    mutate(emailFromUrl);
    setSearchParams({});
  }, [
    emailFromUrl,
    healthError,
    isHealthy,
    isHealthLoading,
    mutate,
    setSearchParams,
  ]);

  // Show unavailable only after health check completes (not while loading)
  const isApiUnavailable = !isHealthLoading && (healthError || !isHealthy);

  return (
    <Box>
      <Typography variant="h4" component="h2" gutterBottom>
        Validate Email Address
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Enter an email address to validate its format, domain, and
        provider-specific rules.
      </Typography>

      {isApiUnavailable && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>API Unavailable</AlertTitle>
          The validation API is currently unavailable. Please try again later.
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <EmailInputForm
          onSubmit={handleValidate}
          isLoading={validateMutation.isPending}
          disabled={isApiUnavailable}
          initialValue={emailFromUrl}
        />
      </Paper>

      {validateMutation.isError && (
        <Alert
          severity={getErrorSeverity(validateMutation.error)}
          sx={{ mb: 3 }}
        >
          <AlertTitle>{getErrorTitle(validateMutation.error)}</AlertTitle>
          {getErrorMessage(validateMutation.error)}
        </Alert>
      )}

      {validateMutation.isSuccess && validateMutation.data && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Validation Result
          </Typography>
          <ValidationResultCard
            result={validateMutation.data as ValidationResult}
            showDetails={true}
          />
        </Box>
      )}
    </Box>
  );
}
